import api from './api';
import { BrandKitUpdate, BrandKitResponse } from '../types/brandKit';

export interface Client {
  id: string;
  name: string;
  slug: string;
  description?: string;
  website?: string;
  email?: string;
  phone?: string;
  logo_url?: string;
  brand_colors?: Record<string, string>;
  settings?: Record<string, any>;
  subscription_tier: string;
  monthly_post_limit: string;
  storage_limit_gb: string;
  created_at: string;
  updated_at: string;
  user_role?: string;
}

export interface ClientUser {
  id: string;
  user_id: string;
  client_id: string;
  role: string;
  created_at: string;
  updated_at: string;
  user_email?: string;
  user_username?: string;
  user_full_name?: string;
}

export interface ClientDetail extends Client {
  users: ClientUser[];
  total_users: number;
  total_posts: number;
  total_media_files: number;
}

export interface ClientListResponse {
  clients: Client[];
  total: number;
  page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface CreateClientData {
  name: string;
  slug: string;
  description?: string;
  website?: string;
  email?: string;
  phone?: string;
  settings?: Record<string, any>;
}

export interface UpdateClientData {
  name?: string;
  description?: string;
  website?: string;
  email?: string;
  phone?: string;
  logo_url?: string;
  brand_colors?: Record<string, string>;
  settings?: Record<string, any>;
}

export interface ClientSwitchResponse {
  client_id: string;
  client_name: string;
  client_slug: string;
  user_role: string;
  permissions: string[];
}

export interface ClientStats {
  client_id: string;
  total_users: number;
  total_posts: number;
  total_media_files: number;
  total_ai_tasks: number;
  storage_used_mb: number;
  posts_this_month: number;
  engagement_rate: number;
  top_performing_posts: any[];
}

class ClientService {
  async getClients(page = 1, perPage = 20, search?: string): Promise<ClientListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }

    const response = await api.get(`/clients/?${params}`);
    return response.data;
  }

  async getClient(clientId: string): Promise<ClientDetail> {
    const response = await api.get(`/clients/${clientId}`);
    return response.data;
  }

  async createClient(data: CreateClientData): Promise<Client> {
    const response = await api.post('/clients/', data);
    return response.data;
  }

  async updateClient(clientId: string, data: UpdateClientData): Promise<Client> {
    const response = await api.put(`/clients/${clientId}`, data);
    return response.data;
  }

  async deleteClient(clientId: string): Promise<void> {
    await api.delete(`/clients/${clientId}`);
  }

  async switchClient(clientId: string): Promise<ClientSwitchResponse> {
    const response = await api.post('/clients/switch', { client_id: clientId });
    return response.data;
  }

  async getBrandKit(clientId: string): Promise<BrandKitResponse> {
    const response = await api.get(`/clients/${clientId}/brand-kit`);
    return response.data;
  }

  async updateBrandKit(clientId: string, data: BrandKitUpdate): Promise<BrandKitResponse> {
    const response = await api.put(`/clients/${clientId}/brand-kit`, data);
    return response.data;
  }

  async addUserToClient(clientId: string, userId: string, role: string): Promise<ClientUser> {
    const response = await api.post(`/clients/${clientId}/users`, {
      user_id: userId,
      role,
    });
    return response.data;
  }

  async updateUserRole(clientId: string, userId: string, role: string): Promise<ClientUser> {
    const response = await api.put(`/clients/${clientId}/users/${userId}`, { role });
    return response.data;
  }

  async removeUserFromClient(clientId: string, userId: string): Promise<void> {
    await api.delete(`/clients/${clientId}/users/${userId}`);
  }

  async getClientStats(clientId: string): Promise<ClientStats> {
    const response = await api.get(`/clients/${clientId}/stats`);
    return response.data;
  }

  async updateClientSettings(clientId: string, settings: Record<string, any>): Promise<Client> {
    const response = await api.put(`/clients/${clientId}/settings`, settings);
    return response.data;
  }
}

export default new ClientService();
export type { BrandKitUpdate, BrandKitResponse };
